import styled from "@emotion/styled";
import { parseGid } from "@shopify/hydrogen-react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  AllCaps,
  Box,
  Button,
  Checkbox,
  Container,
  FieldError,
  FlexRow,
  Link,
  LoadingSpinner,
  Modal,
  RequiredText,
  Stack,
  Text,
  UnexpectedError,
} from "atoms";
import {
  acceptTypes,
  FileInput,
  HiddenInput,
  parseS3URL,
  useFileUpload,
} from "features/images";
import { type GalleryItem, SelectFromPortfolio } from "features/gallery";
import { useToggle } from "utils/hooks";
import { fieldOptions } from "utils/form";
import { paths } from "utils/paths";
import { removeFileExt } from "utils/text";
import {
  type CreateProductReq,
  useCreateProduct,
} from "../../api/useCreateProduct";
import type { CreateStepProps } from "../../types";
import { ProductWizardTemplate } from "../ProductWizardTemplate";

// TODO: Replace with constant when merged
// 100mb = mb * kb * b
const MAX_UPLOAD_SIZE = 100 * 1000 * 1000;

const ACCEPT_TYPE = "stillImage" as const;

type UploadArtworkForm = CreateProductReq & { affirm: boolean };

function CreateStepUploadArtwork({
  artistId,
  productTemplate,
  moveBack,
}: CreateStepProps) {
  const navigate = useNavigate();

  const uploadMutation = useFileUpload();
  const productMutation = useCreateProduct();

  const {
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm<UploadArtworkForm>({
    defaultValues: {
      artistId,
      templateId: productTemplate.id,
    },
  });

  const assetUrl = watch("assetUrl");

  const [isSelecting, toggleIsSelecting] = useToggle(false);

  // Once a file is selected, upload it
  const onChange = (file: File) =>
    uploadMutation.mutate(
      { file },
      {
        onSuccess: ({ url }) => {
          setValue("assetUrl", url, { shouldDirty: true, shouldTouch: true });
          setValue("productName", removeFileExt(file.name));
        },
      },
    );

  // Once a gallery item is selected, update the form
  const onSelectionChange = ([item]: GalleryItem[]) => {
    if (item?.image) {
      setValue("assetUrl", item.image.url, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue("productName", item.title);
      toggleIsSelecting();
    }
  };

  const reset = () => {
    setValue("assetUrl", "");
    setValue("productName", "");
  };

  // Create product after validation
  const onSubmit = handleSubmit(({ affirm: _affirm, ...formData }) =>
    productMutation.mutate(formData, {
      onSuccess: ({ shopifyProductId }) => {
        // On succesful product creation, navigate to Product Wizard to configure it
        navigate(
          paths.artistShopProductEdit(artistId, parseGid(shopifyProductId).id),
        );
      },
    }),
  );

  // Merge states of both mutations to simplify UI logic
  const isLoading = uploadMutation.isLoading || productMutation.isLoading;
  const isError = uploadMutation.isError || productMutation.isError;
  const error = uploadMutation.error ?? productMutation.error;

  return (
    <Form onSubmit={onSubmit}>
      <ProductWizardTemplate
        title="Product Builder"
        subtitle="Upload Image"
        moveBack={moveBack}
      >
        <Container width="sm">
          <Box fullWidth>
            <Stack gap="sm">
              <Text size="sm" bold>
                Image <RequiredText />
              </Text>

              <Text>
                Upload the image you would like to produce and sell as a print.
                You will be able to view mockups and select additional options
                in the next step.
              </Text>

              <FlexRow flexWrap="unset" gap="2rem">
                <UploadBox>
                  {assetUrl ? (
                    <>
                      <img src={parseS3URL(assetUrl)} alt="" />

                      <Button size="xs" variant="blank" onClick={reset}>
                        Reset
                      </Button>
                    </>
                  ) : (
                    <>
                      <div>
                        <FileInput
                          text="Upload From Device"
                          onChange={onChange}
                          accept={ACCEPT_TYPE}
                          maxFileSize={MAX_UPLOAD_SIZE}
                          hideRequirements
                        />

                        <Button
                          size="xs"
                          variant="secondary"
                          onClick={toggleIsSelecting}
                        >
                          Select From Portfolio
                        </Button>
                      </div>

                      <HiddenInput
                        {...register("assetUrl", fieldOptions.required)}
                      />

                      {errors.assetUrl && (
                        <FieldError>You need to select an image.</FieldError>
                      )}
                    </>
                  )}
                </UploadBox>

                <Stack style={{ flex: "unset" }}>
                  <Text size="xs" bold>
                    Image Requirements
                  </Text>

                  <Text size="xxs">
                    Accepted file types:{" "}
                    {acceptTypes[ACCEPT_TYPE].labels.join(", ")}
                    <br />
                    Recommended dimensions: at least 4000px wide
                    <br />
                    Recommended DPI: 300dpi
                  </Text>

                  <Text size="xs" bold>
                    Available Sizes
                  </Text>

                  <Text size="xxs">
                    Your image will be centered and may be cropped to fit
                    multiple sizes and orientations for you to choose from in a
                    later step.
                  </Text>

                  <Text size="xs" bold>
                    Confirm Artwork Ownership <RequiredText />
                  </Text>

                  <Checkbox
                    {...register("affirm", fieldOptions.required)}
                    label={
                      <Text as="span" size="xxs">
                        I confirm that I created and own this original artwork,
                        and that it does not violate <AllCaps>Hug</AllCaps>
                        &apos;s{" "}
                        <Link to="/terms-of-service">Terms of Service</Link>
                      </Text>
                    }
                  />
                  {errors.affirm && (
                    <FieldError>{errors.affirm.message}</FieldError>
                  )}
                </Stack>
              </FlexRow>

              {isError && <UnexpectedError error={error} />}

              {isLoading && (
                <LoadingSpinner
                  text={
                    uploadMutation.isLoading ? "Uploading" : "Creating Product"
                  }
                />
              )}
            </Stack>
          </Box>
        </Container>
      </ProductWizardTemplate>

      <Modal isOpen={isSelecting} onClose={toggleIsSelecting}>
        <SelectFromPortfolio artistId={artistId} onChange={onSelectionChange} />
      </Modal>

      <Footer>
        <Container>
          <Button type="submit" disabled={isLoading}>
            Next
          </Button>
        </Container>
      </Footer>
    </Form>
  );
}

const Form = styled.form({
  minHeight: "80vh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
});

const UploadBox = styled.div(({ theme }) => ({
  width: "30%",
  alignSelf: "flex-start",
  "&>div,&>img": {
    alignItems: "center",
    aspectRatio: "1",
    backgroundColor: theme.colors.gray20,
    borderRadius: theme.borderRadius.md,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.gap,
    justifyContent: "center",
  },
  "&>img": {
    aspectRatio: "auto",
  },
}));

const Footer = styled.footer(({ theme }) => ({
  backgroundColor: theme.colors.accent3L,
  textAlign: "right",
  padding: theme.spacing.md,
}));

export { CreateStepUploadArtwork };
